import { create } from "zustand"

interface User {
  id: string
  email: string
  name: string
  avatar?: string
  subscription: "free" | "starter" | "premium" | "ultimate"
}

interface Profile {
  username: string | null
  usernameChangedAt: string | null
  bio: string | null
  ageVerified: boolean
  tosAccepted: boolean
  onboardingCompleted: boolean
  chatDisplayName: string | null
  privacySettings: {
    show_profile: boolean
    allow_messages: boolean
    share_data: boolean
  }
  chatCoinsUsed: number
  imageGenerationsUsed: number
  contentPreferences: string[] | null
  preferredCharacterGender: "female" | "male" | "any" | null
  preferredLocale: string | null
}

interface AuthState {
  user: User | null
  profile: Profile | null
  isAuthenticated: boolean
  isLoading: boolean
  /** Id of the signed-in user when the stored profile was set. An account switch without sign-out leaves it pointing at the previous user. */
  profileUserId: string | null
  /** Updates user + isAuthenticated only — does not touch isLoading (use setLoading). */
  setUser: (user: User | null) => void
  setProfile: (profile: Profile | null) => void
  setLoading: (loading: boolean) => void
  logout: () => void
}

/** Defer auth store writes to a macrotask — avoids React #310 during concurrent renders. */
export function deferAuthStoreMutation(fn: () => void) {
  setTimeout(fn, 0)
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  profile: null,
  isAuthenticated: false,
  isLoading: true,
  profileUserId: null,
  // Callers must clear loading via setLoading(false); do not fold isLoading into setUser.
  setUser: (user) => set({ user, isAuthenticated: !!user }),
  setProfile: (profile) =>
    set((state) => ({ profile, profileUserId: profile && state.user ? state.user.id : null })),
  setLoading: (isLoading) => set({ isLoading }),
  logout: () =>
    set({ user: null, profile: null, profileUserId: null, isAuthenticated: false, isLoading: false }),
}))
